import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Textarea,
  mergeClasses,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { createCategory, updateCategory } from "api";
import { ConstantValues } from "common/constants";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { ICategory, ICreateCategory } from "models/ICategory";
import { ChangeEvent, useState } from "react";
import { useQueryClient } from "react-query";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useEditCategoryStyles } from "./EditCategory.styles";
import { IEditCategoryProps } from "./EditCategory.types";

export const EditCategory = (props: IEditCategoryProps): JSX.Element => {
  const styles = useEditCategoryStyles();
  const sharedContentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [title, setTitle] = useState<string>(
    props.category?.title || ConstantValues.EMPTY_STRING,
  );
  const [description, setDescription] = useState<string>(
    props.category?.description || ConstantValues.EMPTY_STRING,
  );
  const [isPublic, setIsPublic] = useState<boolean>(
    props.category?.isPublic ?? false,
  );

  const handleTitleChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    setTitle(ev.target.value);
    if (fieldErrors.title) {
      setFieldErrors((prev) => ({ ...prev, title: "" }));
    }
  };

  const handleDescriptionChange = (
    ev: ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setDescription(ev.target.value);
    if (fieldErrors.description) {
      setFieldErrors((prev) => ({ ...prev, description: "" }));
    }
  };

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!title.trim()) {
      errors.title = "Title is required";
    } else if (title.trim().length > 50) {
      errors.title = "Title cannot be more than 50 characters";
    } else {
      const titleLowerCase = title.trim().toLowerCase();
      const existingCategory = props.categories.find(
        (category) => category.title.trim().toLowerCase() === titleLowerCase,
      );

      if (!props.category && existingCategory) {
        errors.title = `A category with the title "${title}" already exists`;
      } else if (
        existingCategory &&
        existingCategory.id !== props.category?.id
      ) {
        errors.title = `A category with the title "${title}" already exists`;
      }
    }

    if (description.length > 200) {
      errors.description = "Description cannot have more than 200 characters";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      if (props.category) {
        const updatedCategory: ICategory = {
          ...props.category,
          title,
          description,
          isPublic,
        };

        await updateCategory({ category: updatedCategory });
      } else {
        const newCategory: ICreateCategory = {
          title,
          description,
          tags: [],
        };

        await createCategory({ category: newCategory });
      }

      await queryClient.invalidateQueries("categories");
      props.onDismiss(true);
    } catch (error) {
      console.error("Error saving category:", error);
      setFieldErrors({
        general: "An error occurred while saving. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasIncompleteDetails = !title.trim();

  const dialogTitle = props.category
    ? "Update Knowledge Components Category"
    : "Add Knowledge Components Category";

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss(false);
      }}
    >
      <DialogSurface
        className={mergeClasses(
          styles.dialogSurface,
          footerStyles.dialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => props.onDismiss(false)}
              />
            }
          >
            {dialogTitle}
          </DialogTitle>

          <DialogContent
            className={mergeClasses(
              styles.dialogContent,
              sharedContentStyles.formContainer,
            )}
          >
            {isLoading && (
              <div className={sharedContentStyles.formLoadingOverlay}>
                <Spinner label="Saving category..." />
              </div>
            )}
            <Field
              label="Title"
              required
              validationState={fieldErrors.title ? "error" : "none"}
              validationMessage={fieldErrors.title}
            >
              <Input
                placeholder="Enter category title"
                value={title}
                onChange={handleTitleChange}
                disabled={isLoading}
              />
            </Field>

            <Field
              label="Description"
              validationState={fieldErrors.description ? "error" : "none"}
              validationMessage={fieldErrors.description}
            >
              <Textarea
                placeholder="Enter description"
                value={description}
                resize="vertical"
                onChange={handleDescriptionChange}
                disabled={isLoading}
              />
            </Field>

            <CustomToggle
              checked={isPublic}
              onChange={setIsPublic}
              label="Is Public"
              hint="Public categories are visible to all users"
              disabled={isLoading}
            />

            {fieldErrors.general && (
              <div className={sharedContentStyles.feedbackError} role="alert">
                {fieldErrors.general}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSave}
              dimmed={hasIncompleteDetails}
              tooltip="Save"
              ariaLabel="Save category"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
